import { useCallback, useEffect, useRef, useState } from 'react'
import { PressScale } from '../../lib/PressScale'
import { feedback } from '../../lib/feedback'
import { toast } from '../../lib/toast'
import { staggerProps } from '../../lib/stagger'
import { AnimatedNumber } from '../../lib/AnimatedNumber'
import { formatPrice, formatDate } from '../../lib/format'
import { isNativeShell } from '../../mobile/lib/appMode'
import {
  ASKA_PLUS_PLAN, canJoinAskaPlus, formatAskaPlusPrice, askaPlusPriceLabel,
  membershipUiState, type AskaPlusMembership, type MembershipUiState,
} from '../../lib/askaPlus'
import { ASKA_SERVICES_FEE_PERCENT } from '../../lib/askaServices'
import { FLAT_PRICING, formatFlatUsd, formatGuaranteeRate } from '../../lib/flatPricing'
import { askaPlusApi, type AskaPlusState, type AskaPlusInvoice } from '../../api/endpoints'
import { ApiError } from '../../api/types'

/** Read a `{ code }` off an ApiError body, when present. */
function errorCode(err: unknown): string | null {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const c = (err.body as { code?: unknown }).code
    return typeof c === 'string' ? c : null
  }
  return null
}

/**
 * The Aska+ membership surface — shell-agnostic, wrapped by the desktop route
 * (`/account/membership`) and the mobile screen. The twin of `team/TeamPanel`.
 *
 * ── NO DEAD ENDS (docs/ORG-FRONTEND-PLAN.md house law) ────────────────────
 * Every state has a visible way forward, and none of them is "sign out":
 *   not_member          → the join card (the three levers, in money, + join)
 *   active              → manage: renewal date, invoices, cancel — NOT re-offer
 *   cancelling          → "active until X, then lapses" + Reactivate
 *   past_due            → "your card was declined" + Update payment
 *   incomplete          → the first charge never cleared + retry/join
 *   native shell        → read-only, "manage on the web" (App Store IAP rule)
 *   billing-not-armed   → the price + pitch, no join button, the server message
 * A membership screen that can strand a user in any of these is not done.
 *
 * ── PREMIUM, ANIMATED (owner pass/fail requirement) ───────────────────────
 * The join price counts up (AnimatedNumber, .tabular), the join confirmation is
 * a one-settle `.crest-strike` moment paired with `feedback.success()`, rows
 * stagger in, and every state transition is smooth. Reduced-motion is respected
 * (AnimatedNumber snaps; the CSS classes have their own reduce blocks).
 */
export function MembershipPanel({ onBack }: { onBack?: () => void }) {
  const [state, setState] = useState<AskaPlusState | null>(null)
  const [invoices, setInvoices] = useState<AskaPlusInvoice[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<null | 'join' | 'portal' | 'cancel' | 'reactivate'>(null)
  const [error, setError] = useState<string | null>(null)
  // The just-joined celebration — set when we land back with ?membership=joined
  // AND the server confirms an active membership.
  const [celebrate, setCelebrate] = useState(false)
  const celebratedFor = useRef<string | null>(null)

  const load = useCallback(async () => {
    try {
      const s = await askaPlusApi.get()
      setState(s)
      // Fetch invoices only when there's a billing account to list (a member, or
      // a lapsed one). A never-joined buyer needs no receipt list.
      if (s.membership.manageable) {
        askaPlusApi.invoices().then((r) => setInvoices(r.invoices)).catch(() => setInvoices([]))
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load your membership.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  // Re-render when `/api/health` re-points the plan mid-session — exactly like
  // SeatPaywall. `canJoinAskaPlus()` reads the ASKA_PLUS_PLAN live binding, whose
  // seed default is `live:false`; if this panel painted before the fire-and-forget
  // boot fetch hydrated the plan, the JoinCard would show a stale "not switched
  // on" state that never self-heals. Bumping on the plan-changed event fixes that.
  const [, bump] = useState(0)
  useEffect(() => {
    const onPlan = () => bump((n) => n + 1)
    window.addEventListener('aska:plus-plan-changed', onPlan)
    return () => window.removeEventListener('aska:plus-plan-changed', onPlan)
  }, [])

  // The abandon path (Stripe CANCEL_URL) returns to ?membership=cancelled — the
  // panel already renders the correct not_member state, so just strip the param
  // so it doesn't strand in the address bar. The 'joined' case is cleaned by the
  // celebrate effect below (which also fires the moment).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('membership') !== 'cancelled') return
    params.delete('membership')
    const qs = params.toString()
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''))
  }, [])

  // Celebrate a fresh join exactly once. The success return URL carries
  // ?membership=joined; the moment only fires if the server also agrees the
  // membership is active (a cancelled return must not celebrate).
  useEffect(() => {
    if (!state?.membership.active) return
    const params = new URLSearchParams(window.location.search)
    if (params.get('membership') !== 'joined') return
    if (celebratedFor.current === 'joined') return
    celebratedFor.current = 'joined'
    setCelebrate(true)
    feedback.success()
    // Clean the URL so a refresh doesn't re-celebrate.
    params.delete('membership')
    const qs = params.toString()
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''))
  }, [state?.membership.active])

  const membership = state?.membership ?? null
  const ui: MembershipUiState = membershipUiState(membership)
  const configured = state?.configured ?? false
  const native = isNativeShell()

  async function join() {
    setError(null)
    if (!canJoinAskaPlus()) {
      // Native shell (or inert rail) — never a dead end: explain where.
      toast(native ? 'Manage your Aska+ membership on the web at thediaex.com.' : (state?.message ?? 'Aska+ isn’t switched on yet.'), 'info')
      return
    }
    setBusy('join')
    try {
      const res = await askaPlusApi.checkoutSession()
      if (res.url) { window.location.assign(res.url); return }
      // {configured:false} or a 409 that came back as a body — render the message.
      toast(res.message ?? 'Aska+ isn’t switched on yet.', 'info')
      await load()
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Could not start checkout — please try again.'
      // An already_member 409 is not an error to the user — it means it worked.
      if (errorCode(err) === 'already_member') { toast('You’re already an Aska+ member.', 'success'); await load() }
      else setError(msg)
    } finally { setBusy(null) }
  }

  async function openPortal() {
    if (!canJoinAskaPlus()) { toast(native ? 'Manage your membership on the web at thediaex.com.' : 'Billing isn’t switched on yet.', 'info'); return }
    setBusy('portal')
    try {
      const res = await askaPlusApi.portal()
      if (res.url) { window.location.assign(res.url); return }
      toast(res.message ?? 'There’s nothing to manage yet.', 'info')
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not open the billing portal.', 'error')
    } finally { setBusy(null) }
  }

  async function cancel() {
    if (!canJoinAskaPlus()) { toast(native ? 'Manage your membership on the web.' : 'Billing isn’t switched on yet.', 'info'); return }
    setBusy('cancel')
    try {
      const res = await askaPlusApi.cancel()
      setState(res)
      toast('Your membership will stay active until the end of your billing period.', 'success')
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not cancel — please try again.', 'error')
    } finally { setBusy(null) }
  }

  async function reactivate() {
    if (!canJoinAskaPlus()) { toast('Manage your membership on the web.', 'info'); return }
    setBusy('reactivate')
    try {
      const res = await askaPlusApi.reactivate()
      setState(res)
      feedback.success()
      toast('Welcome back — your Aska+ membership will renew as normal.', 'success')
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not reactivate — please try again.', 'error')
    } finally { setBusy(null) }
  }

  if (loading) {
    return <div className="py-16 text-center text-navy-300 tabular">Loading your membership…</div>
  }

  return (
    <div className="mx-auto max-w-2xl px-4 pb-24 pt-6">
      {onBack && (
        <button onClick={onBack} className="mb-4 text-sm text-navy-300 hover:text-ink">← Back</button>
      )}

      <MembershipHeader ui={ui} celebrate={celebrate} />

      {error && (
        <div className="fb-deny mb-4 rounded-lg border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger" role="alert">
          {error}
        </div>
      )}

      {ui === 'not_member' && (
        <JoinCard
          native={native}
          message={state?.message ?? null}
          busy={busy === 'join'}
          onJoin={join}
        />
      )}

      {(ui === 'active' || ui === 'cancelling') && membership && (
        <ManageActive
          membership={membership}
          cancelling={ui === 'cancelling'}
          busy={busy}
          native={native}
          onPortal={openPortal}
          onCancel={cancel}
          onReactivate={reactivate}
        />
      )}

      {ui === 'past_due' && membership && (
        <PastDue membership={membership} busy={busy === 'portal'} native={native} onUpdatePayment={openPortal} />
      )}

      {ui === 'incomplete' && (
        <Incomplete configured={configured} native={native} busy={busy === 'join'} onRetry={join} onPortal={openPortal} />
      )}

      {membership?.manageable && invoices !== null && (
        <InvoiceList invoices={invoices} />
      )}

      <WhatYouGet />
    </div>
  )
}

/* ─────────────────────────────── header ──────────────────────────────────── */

function MembershipHeader({ ui, celebrate }: { ui: MembershipUiState; celebrate: boolean }) {
  const active = ui === 'active' || ui === 'cancelling'
  return (
    <header className="mb-6">
      <div className="flex items-center gap-3">
        <AskaPlusMark strike={celebrate} active={active} />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink">Aska+</h1>
          <p className="text-sm text-navy-300">
            {active
              ? (celebrate ? 'You’re in. Welcome to Aska+.' : 'Your membership')
              : FLAT_PRICING.live
                ? 'Members source free on every order — and ship free.'
                : `Members pay ${ASKA_PLUS_PLAN.memberFeePercent}% — and ship free.`}
          </p>
        </div>
      </div>
    </header>
  )
}

/** The Aska+ gold mark. On a fresh join it plays the one-settle `.crest-strike`
 *  arrival — a moment, never a loop. */
function AskaPlusMark({ strike, active }: { strike: boolean; active: boolean }) {
  return (
    <span
      className={`inline-flex h-11 w-11 items-center justify-center rounded-full text-lg font-bold ${strike ? 'crest-strike' : ''} ${active ? 'bg-gold-500 text-navy-900' : 'bg-navy-900 text-gold-300'}`}
      aria-hidden="true"
    >
      +
    </span>
  )
}

/* ───────────────────────────── join card ─────────────────────────────────── */

function JoinCard({ native, message, busy, onJoin }: {
  native: boolean; message: string | null; busy: boolean; onJoin: () => void
}) {
  const canJoin = canJoinAskaPlus()
  return (
    <section className="card order-aura mb-6 p-6">
      <div className="flex items-baseline justify-between">
        <h2 className="text-lg font-semibold text-ink">Join Aska+</h2>
        <div className="text-right">
          <span className="tabular text-2xl font-bold text-ink">
            <AnimatedNumber
              value={ASKA_PLUS_PLAN.priceCents / 100}
              format={(n) => formatAskaPlusPrice(Math.round(n * 100))}
              reserve="$188.88"
              animateOnMount
            />
          </span>
          <span className="text-sm text-navy-300">/mo</span>
        </div>
      </div>

      <ThreeLevers />

      <div className="mt-5">
        {canJoin ? (
          <PressScale feel="tap" onClick={onJoin} disabled={busy}
            className="btn-primary w-full justify-center py-3 text-base font-semibold">
            {busy ? 'Starting checkout…' : `Join Aska+ for ${askaPlusPriceLabel()}`}
          </PressScale>
        ) : (
          <div className="rounded-lg border border-line bg-cream px-4 py-3 text-sm text-navy-500">
            {native
              ? 'Aska+ is managed on the web. Visit thediaex.com to join.'
              : (message ?? 'Aska+ isn’t switched on yet — we’ll let you know the moment it is.')}
          </div>
        )}
      </div>
      {canJoin && (
        <p className="mt-3 text-center text-xs text-navy-300">Cancel anytime. Renews monthly at {askaPlusPriceLabel()}.</p>
      )}
    </section>
  )
}

/** The value levers, in plain money terms (the DoorDash DashPass pitch).
 *  FLAT model (FLAT_PRICING_LIVE): free sourcing on EVERY order + the 2%
 *  return-protection rate + Aska Credit replace the dead "12% down from 15%"
 *  points-off pitch. */
function ThreeLevers() {
  if (FLAT_PRICING.live) {
    const levers = [
      { icon: '✓', title: 'Free sourcing on every order', body: `Skip the ${formatFlatUsd(FLAT_PRICING.serviceFeeUsd)} service fee — every order, not just your first ${FLAT_PRICING.freeSourcesIncluded}.` },
      { icon: '%', title: `Return protection at ${formatGuaranteeRate(true)}`, body: `Members insure any stone at ${formatGuaranteeRate(true)} instead of ${formatGuaranteeRate(false)} — replace or reprice if it doesn't match grade.` },
      { icon: '◆', title: 'Aska Credit access', body: 'Net-terms financing on eligible orders — buy now, settle on terms.' },
      { icon: '⇢', title: 'Free shipping', body: `Saves ${formatPrice(49.99)} on every order under ${formatPrice(5000)}.` },
    ]
    return (
      <ul className="mt-4 space-y-3">
        {levers.map((l, i) => (
          <li key={l.title} {...staggerProps(i, { className: 'flex gap-3' })}>
            <span className="mt-0.5 inline-flex h-7 w-7 flex-none items-center justify-center rounded-full bg-gold-050 text-sm font-bold text-gold-600">{l.icon}</span>
            <div>
              <p className="text-sm font-medium text-ink">{l.title}</p>
              <p className="text-xs text-navy-300">{l.body}</p>
            </div>
          </li>
        ))}
      </ul>
    )
  }
  const pointsOff = Math.max(0, ASKA_SERVICES_FEE_PERCENT - ASKA_PLUS_PLAN.memberFeePercent)
  const levers = [
    { icon: '%', title: `${ASKA_PLUS_PLAN.memberFeePercent}% Aska Services fee`, body: `Down from ${ASKA_SERVICES_FEE_PERCENT}% — ${pointsOff} points off every order.` },
    { icon: '✓', title: 'QC discount', body: 'Members pay less for the quality-check add-on.' },
    { icon: '⇢', title: 'Free shipping', body: `Saves ${formatPrice(49.99)} on every order under ${formatPrice(5000)}.` },
  ]
  return (
    <ul className="mt-4 space-y-3">
      {levers.map((l, i) => (
        <li key={l.title} {...staggerProps(i, { className: 'flex gap-3' })}>
          <span className="mt-0.5 inline-flex h-7 w-7 flex-none items-center justify-center rounded-full bg-gold-050 text-sm font-bold text-gold-600">{l.icon}</span>
          <div>
            <p className="text-sm font-medium text-ink">{l.title}</p>
            <p className="text-xs text-navy-300">{l.body}</p>
          </div>
        </li>
      ))}
    </ul>
  )
}

/* ─────────────────────────── manage (active) ─────────────────────────────── */

function ManageActive({ membership, cancelling, busy, native, onPortal, onCancel, onReactivate }: {
  membership: AskaPlusMembership; cancelling: boolean; busy: string | null; native: boolean
  onPortal: () => void; onCancel: () => void; onReactivate: () => void
}) {
  const [confirmCancel, setConfirmCancel] = useState(false)
  const renews = membership.currentPeriodEnd ? formatDate(membership.currentPeriodEnd) : null
  return (
    <section className="card mb-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-gold-050 px-2.5 py-1 text-xs font-semibold text-gold-600">
            <span className="h-1.5 w-1.5 rounded-full bg-success" /> {cancelling ? 'Active — ending' : 'Active member'}
          </span>
          <p className="mt-2 text-sm text-navy-500">
            {cancelling
              ? (renews ? `Your membership stays active until ${renews}, then lapses.` : 'Your membership is scheduled to end at the period end.')
              : (renews ? `Renews ${renews} at ${askaPlusPriceLabel()}.` : `Renews monthly at ${askaPlusPriceLabel()}.`)}
          </p>
        </div>
      </div>

      <div className="mt-5 space-y-2">
        {cancelling ? (
          <PressScale feel="tap" onClick={onReactivate} disabled={busy === 'reactivate' || native}
            className="btn-primary w-full justify-center py-2.5">
            {busy === 'reactivate' ? 'Reactivating…' : 'Reactivate membership'}
          </PressScale>
        ) : (
          <>
            {membership.manageable && (
              <PressScale feel="tap" onClick={onPortal} disabled={busy === 'portal'}
                className="btn-secondary w-full justify-center py-2.5">
                {busy === 'portal' ? 'Opening…' : 'Manage payment & receipts'}
              </PressScale>
            )}
            {!confirmCancel ? (
              <button onClick={() => setConfirmCancel(true)} disabled={native}
                className="w-full py-2.5 text-sm text-navy-300 hover:text-danger">
                Cancel membership
              </button>
            ) : (
              <div className="rounded-lg border border-line bg-cream p-3">
                <p className="text-sm text-navy-500">
                  {FLAT_PRICING.live
                    ? <>You’ll keep Aska+ {renews ? `until ${renews}` : 'until your period ends'} — free sourcing, the {formatGuaranteeRate(true)} protection rate, and free shipping stay active until then.</>
                    : <>You’ll keep Aska+ {renews ? `until ${renews}` : 'until your period ends'} — the {ASKA_PLUS_PLAN.memberFeePercent}% fee, free shipping, and QC discount stay active until then.</>}
                </p>
                <div className="mt-3 flex gap-2">
                  <PressScale feel="deny" onClick={onCancel} disabled={busy === 'cancel'}
                    className="btn-secondary flex-1 justify-center py-2 text-sm">
                    {busy === 'cancel' ? 'Cancelling…' : 'Confirm cancel'}
                  </PressScale>
                  <button onClick={() => setConfirmCancel(false)} className="flex-1 py-2 text-sm text-navy-300 hover:text-ink">
                    Keep membership
                  </button>
                </div>
              </div>
            )}
          </>
        )}
        {native && (
          <p className="pt-1 text-center text-xs text-navy-300">Manage or cancel your membership on the web at thediaex.com.</p>
        )}
      </div>
    </section>
  )
}

/* ─────────────────────────────── past_due ────────────────────────────────── */

function PastDue({ membership, busy, native, onUpdatePayment }: {
  membership: AskaPlusMembership; busy: boolean; native: boolean; onUpdatePayment: () => void
}) {
  const graceEnds = membership.currentPeriodEnd ? formatDate(membership.currentPeriodEnd) : null
  return (
    <section className="card mb-6 border-warning/40 p-6">
      <span className="inline-flex items-center gap-1.5 rounded-full bg-warning/10 px-2.5 py-1 text-xs font-semibold text-warning">
        <span className="h-1.5 w-1.5 rounded-full bg-warning" /> Payment failed
      </span>
      <p className="mt-2 text-sm text-navy-500">
        Your last Aska+ payment was declined. Your member benefits are still active for now{graceEnds ? ` (through ${graceEnds})` : ''} — update your card to keep them.
      </p>
      <div className="mt-4">
        {!native ? (
          <PressScale feel="tap" onClick={onUpdatePayment} disabled={busy}
            className="btn-primary w-full justify-center py-2.5">
            {busy ? 'Opening…' : 'Update payment method'}
          </PressScale>
        ) : (
          <p className="text-sm text-navy-300">Update your card on the web at thediaex.com to keep your benefits.</p>
        )}
      </div>
    </section>
  )
}

/* ─────────────────────────────── incomplete ──────────────────────────────── */

function Incomplete({ configured, native, busy, onRetry, onPortal }: {
  configured: boolean; native: boolean; busy: boolean; onRetry: () => void; onPortal: () => void
}) {
  return (
    <section className="card mb-6 p-6">
      <h2 className="text-lg font-semibold text-ink">Finish joining Aska+</h2>
      <p className="mt-2 text-sm text-navy-500">
        Your first payment didn’t go through, so your membership isn’t active yet. Try again — you’ll only be charged once it succeeds.
      </p>
      <div className="mt-4 space-y-2">
        {canJoinAskaPlus() ? (
          <PressScale feel="tap" onClick={onRetry} disabled={busy}
            className="btn-primary w-full justify-center py-2.5">
            {busy ? 'Starting…' : `Try again — ${askaPlusPriceLabel()}`}
          </PressScale>
        ) : (
          <p className="text-sm text-navy-300">{native ? 'Join Aska+ on the web at thediaex.com.' : 'Aska+ isn’t switched on yet.'}</p>
        )}
        {configured && !native && (
          <button onClick={onPortal} className="w-full py-2 text-sm text-navy-300 hover:text-ink">Check my payment status</button>
        )}
      </div>
    </section>
  )
}

/* ─────────────────────────────── invoices ────────────────────────────────── */

function InvoiceList({ invoices }: { invoices: AskaPlusInvoice[] }) {
  if (invoices.length === 0) {
    return (
      <section className="card mb-6 p-6">
        <h2 className="text-sm font-semibold text-ink">Receipts</h2>
        <p className="mt-2 text-sm text-navy-300">No invoices yet — your first receipt appears here after your first payment.</p>
      </section>
    )
  }
  return (
    <section className="card mb-6 p-6">
      <h2 className="mb-3 text-sm font-semibold text-ink">Receipts</h2>
      <ul className="divide-y divide-line">
        {invoices.map((inv, i) => (
          <li key={inv.id} {...staggerProps(i, { className: 'flex items-center justify-between py-2.5' })}>
            <div>
              <p className="text-sm text-ink tabular">{inv.createdIso ? formatDate(inv.createdIso) : (inv.number ?? inv.id)}</p>
              <p className="text-xs capitalize text-navy-300">{inv.status ?? 'paid'}</p>
            </div>
            <div className="flex items-center gap-3">
              {inv.amountCents != null && (
                <span className="tabular text-sm font-medium text-ink">{formatPrice(inv.amountCents / 100)}</span>
              )}
              {(inv.hostedInvoiceUrl || inv.pdfUrl) && (
                <a href={inv.hostedInvoiceUrl ?? inv.pdfUrl ?? '#'} target="_blank" rel="noreferrer"
                  className="text-xs font-medium text-sapphire hover:underline">View</a>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

/* ───────────────────────────── what you get ──────────────────────────────── */

/** Always-visible reassurance of the value, below the fold, for members and
 *  non-members alike (a member re-reads why they pay; a non-member is reminded
 *  even when the join card is above). */
function WhatYouGet() {
  return (
    <section className="rounded-xl border border-line bg-cream/60 p-5">
      <h2 className="text-sm font-semibold text-ink">What Aska+ includes</h2>
      <ThreeLevers />
    </section>
  )
}
